import Icon from '@ant-design/icons';
import { css } from '@emotion/css';
import { Popover, PopoverProps, Tooltip } from 'antd';
import { LocalMessage } from 'hooks/business/conversation';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useTranslate } from '@tolgee/react';
import { TranslateMode } from 'services/api/overseaTranslate';
import sysLangToTranslationLang from 'utils/sysLangToTranslationLang';
import { ReactComponent as GoogleSvg } from '../../../assets/google.svg';
import { ReactComponent as GptSvg } from '../../../assets/gpt.svg';
import { ReactComponent as DeepseekSvg } from '../../../assets/deepseek.svg';
import { ReactComponent as ChatPlusAISvg } from '../../../assets/chatplusai.svg';
import { ReactComponent as HideTransSvg } from '../../../assets/hide_translate.svg';
import { useMemo } from 'react';
import { useUpdateMessage } from 'hooks/business/message';

export const isDisableTranslate = (message: LocalMessage) => {
  return (
    message.textContent?.autoTranslation?.status === 'error' ||
    message.textContent?.autoTranslation?.status === 'pending'
  );
};

export const isMessageTranslated = (message: LocalMessage, targetLanguage: General.Language) => {
  return Boolean(
    message.textContent?.translationContent?.[sysLangToTranslationLang(targetLanguage)]?.text,
  );
};

export const useIsMessageTranslated = (
  message: LocalMessage,
  targetLanguage?: General.Language,
) => {
  const systemLanguage = useSystemLanguageValue();

  return isMessageTranslated(message, targetLanguage || systemLanguage);
};

export interface TranslatePopoverProps extends PopoverProps {
  message: LocalMessage;
  targetLanguage: General.Language;
  onTranslate: (
    message: LocalMessage,
    lang: General.Language,
    mode: TranslateMode,
    translated: boolean,
  ) => void;
}

function TranslatePopover(props: TranslatePopoverProps) {
  const { message, targetLanguage, onTranslate, ...otherProps } = props;

  const { t } = useTranslate();

  const messageTranslated = useIsMessageTranslated(message, targetLanguage);
  const updateMessage = useUpdateMessage();
  const sysLang = useSystemLanguageValue();

  /** 隐藏翻译按钮 */
  const hideTranslateBtnVisible = useMemo(() => {
    return (
      !message.hideTranslate &&
      (message.textContent?.translationContent?.[sysLangToTranslationLang(sysLang)] ||
        message.textContent?.originalText)
    );
  }, [message, sysLang]);

  const content = (
    <>
      {(
        [
          { title: 'Google', mode: 'GOOGLE', icon: GoogleSvg },
          { title: 'ChatGPT', mode: 'CHAT_GPT', icon: GptSvg },
          { title: 'DeepSeek', mode: 'DEEPSEEK', icon: DeepseekSvg },
          { title: 'ChatPlusAI', mode: 'CHATPLUSAI', icon: ChatPlusAISvg },
        ] as const
      ).map(({ title, mode, icon }) => {
        return (
          <Tooltip
            key={mode}
            getTooltipContainer={(_) => _}
            title={
              messageTranslated
                ? t('ViewsChat.使用x重新翻译', { x: title })
                : t('ViewsChat.使用x翻译', { x: title })
            }
          >
            <div
              onClick={(e) => {
                e.stopPropagation();
                if (isDisableTranslate(message)) {
                  return;
                }

                onTranslate(message, targetLanguage, mode, messageTranslated);
              }}
            >
              <Icon component={icon} />
            </div>
          </Tooltip>
        );
      })}
      {hideTranslateBtnVisible && (
        <Tooltip title={t('hide_translate')}>
          <div
            onClick={() => {
              updateMessage(message.msgId, (prev) => {
                if (!prev) {
                  return undefined;
                }
                return {
                  ...prev,
                  hideTranslate: true,
                };
              });
            }}
          >
            <Icon component={HideTransSvg} />
          </div>
        </Tooltip>
      )}
    </>
  );

  return (
    <Popover
      placement="bottom"
      align={{ offset: [0, -4] }}
      overlayClassName={styles.popover}
      content={content}
      {...otherProps}
    />
  );
}

const styles = {
  popover: css`
    .ant-popover-arrow {
      display: none;
    }
    .ant-popover-inner-content {
      display: flex;
      align-items: center;
      padding: 4px;
      > div {
        width: 24px;
        height: 24px;
        line-height: 24px;
        text-align: center;
        font-size: 16px;
        cursor: pointer;
        &:hover {
          background: #f0f2f5;
        }
      }
    }
  `,
};

export default TranslatePopover;
